import { ApplicationController } from "../../support/application-controller";

function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
}

function statusBadge(status) {
    var cssStatus = String(status || '').replace(/_/g, '-');
    return '<span class="hubspot-tools__status-badge hubspot-tools__status-badge--' + escapeHtml(cssStatus) + '">' + escapeHtml(status) + '</span>';
}

export default class extends ApplicationController {
    static targets = [
        "modelSelect",
        "dryRunCheckbox",
        "statsButton",
        "syncButton",
        "statusMessage",
        "statsPanel",
        "statsContent",
        "progressPanel",
        "progressBar",
        "progressText",
        "resultsPanel",
        "resultsContent"
    ];

    connect() {
        this.polling = false;
        this.checkInitialStatus();
    }

    disconnect() {
        this.stopPolling();
    }

    fetchStats(event) {
        if (event) event.preventDefault();
        this.disableButtons();
        this.showMessage("Gathering statistics...");

        fetch("/admin/hubspot/sync/stats", {
            headers: { "Accept": "application/json", "X-CSRF-Token": this.getCSRFToken() },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                this.enableButtons();
                if (data.success && data.stats) {
                    this.renderStats(data.stats);
                } else {
                    this.showMessage("Failed: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.enableButtons();
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    startSync(event) {
        if (event) event.preventDefault();

        var model = this.hasModelSelectTarget ? this.modelSelectTarget.value : "all";
        var dryRun = this.hasDryRunCheckboxTarget ? this.dryRunCheckboxTarget.checked : true;

        if (!dryRun && !confirm("This will push property values to HubSpot for " + (model === "all" ? "all models" : model) + ". Continue?")) {
            return;
        }

        this.disableButtons();
        this.showProgress();
        this.showMessage("Enqueuing sync job...");

        fetch("/admin/hubspot/sync/run", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this.getCSRFToken()
            },
            credentials: "same-origin",
            body: JSON.stringify({ model: model, dry_run: dryRun })
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success) {
                    this.startPolling();
                } else {
                    this.enableButtons();
                    this.showMessage("Failed: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.enableButtons();
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    // Private

    checkInitialStatus() {
        fetch("/admin/hubspot/sync/status", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.status === "running") {
                    this.disableButtons();
                    this.showProgress();
                    this.updateProgress(data);
                    this.startPolling();
                } else if (data.status === "completed" || data.status === "failed") {
                    this.showMessage(data.message || data.status);
                    if (data.results) this.renderResults(data.results);
                }
            }.bind(this))
            .catch(function() {});
    }

    startPolling() {
        if (this.polling) return;
        this.polling = true;
        this.pollInterval = setInterval(function() { this.poll(); }.bind(this), 3000);
    }

    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this.polling = false;
    }

    poll() {
        fetch("/admin/hubspot/sync/status", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                this.updateProgress(data);
                if (data.status !== "running") {
                    this.stopPolling();
                    this.enableButtons();
                    if (data.results) this.renderResults(data.results);
                }
            }.bind(this))
            .catch(function(err) {
                console.error("Poll failed:", err);
            });
    }

    updateProgress(data) {
        if (this.hasStatusMessageTarget) {
            this.statusMessageTarget.textContent = data.message || "";
        }
        if (data.progress && this.hasProgressBarTarget && this.hasProgressTextTarget) {
            var pct = data.progress.total > 0
                ? Math.round((data.progress.processed / data.progress.total) * 100)
                : 0;
            this.progressBarTarget.style.width = pct + "%";
            this.progressTextTarget.textContent = data.progress.processed + " / " + data.progress.total;
        }
    }

    renderStats(stats) {
        if (!this.hasStatsPanelTarget || !this.hasStatsContentTarget) return;

        this.statsPanelTarget.style.display = "block";

        var html = '<table class="hubspot-tools__results-table">';
        html += '<thead><tr><th>Model</th><th>HubSpot Type</th><th>Properties</th><th>Total Records</th><th>Mapped</th></tr></thead><tbody>';

        var models = stats.models || [];
        models.forEach(function(m) {
            html += '<tr>';
            html += '<td>' + escapeHtml(m.model) + '</td>';
            html += '<td>' + escapeHtml(m.object_type) + '</td>';
            html += '<td>' + escapeHtml((m.properties || []).join(', ')) + '</td>';
            html += '<td>' + escapeHtml(String(m.total_records)) + '</td>';
            html += '<td>' + escapeHtml(String(m.mapped_records)) + '</td>';
            html += '</tr>';
        });

        html += '</tbody></table>';
        this.statsContentTarget.innerHTML = html;
        this.showMessage("Statistics loaded.");
    }

    renderResults(results) {
        if (!this.hasResultsPanelTarget || !this.hasResultsContentTarget) return;

        this.resultsPanelTarget.style.display = "block";
        if (this.hasProgressPanelTarget) this.progressPanelTarget.style.display = "none";

        var html = '<div class="hubspot-tools__results">';
        html += '<p><strong>Mode:</strong> ' + escapeHtml(results.dry_run ? 'Dry run' : 'Live') + '</p>';

        if (results.total_synced !== undefined) {
            html += '<p>Synced: ' + escapeHtml(String(results.total_synced)) +
                ' | Skipped: ' + escapeHtml(String(results.total_skipped)) +
                ' | Errors: ' + escapeHtml(String(results.total_errors)) + '</p>';
        }

        if (results.models && results.models.length > 0) {
            html += '<table class="hubspot-tools__results-table">';
            html += '<thead><tr><th>Model</th><th>HubSpot Type</th><th>Status</th><th>Mapped</th><th>Synced</th><th>Skipped</th><th>Errors</th></tr></thead><tbody>';

            results.models.forEach(function(m) {
                html += '<tr>';
                html += '<td>' + escapeHtml(m.model) + '</td>';
                html += '<td>' + escapeHtml(m.object_type) + '</td>';
                html += '<td>' + statusBadge(m.status) + '</td>';
                html += '<td>' + escapeHtml(String(m.mapped_records)) + '</td>';
                html += '<td>' + escapeHtml(String(m.synced)) + '</td>';
                html += '<td>' + escapeHtml(String(m.skipped)) + '</td>';
                html += '<td>' + escapeHtml(String(m.errors)) + '</td>';
                html += '</tr>';
                if (m.error) {
                    html += '<tr><td colspan="7" style="color: #d93025; font-size: 12px;">' + escapeHtml(m.error) + '</td></tr>';
                }
            });

            html += '</tbody></table>';
        }

        html += '</div>';
        this.resultsContentTarget.innerHTML = html;
    }

    showProgress() {
        if (this.hasProgressPanelTarget) this.progressPanelTarget.style.display = "block";
        if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "0%";
        if (this.hasProgressTextTarget) this.progressTextTarget.textContent = "";
    }

    showMessage(msg) {
        if (this.hasStatusMessageTarget) this.statusMessageTarget.textContent = msg;
    }

    disableButtons() {
        if (this.hasStatsButtonTarget) this.statsButtonTarget.disabled = true;
        if (this.hasSyncButtonTarget) this.syncButtonTarget.disabled = true;
    }

    enableButtons() {
        if (this.hasStatsButtonTarget) this.statsButtonTarget.disabled = false;
        if (this.hasSyncButtonTarget) this.syncButtonTarget.disabled = false;
    }

    getCSRFToken() {
        var meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute("content") : "";
    }
}
